import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { Box, Tooltip } from '@mui/material';
import { isFinite } from 'lodash';
import cx from 'classnames';
import CommentOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import StarOutlinedIcon from '@mui/icons-material/StarOutlineRounded';
import SelectedIcon from '@mui/icons-material/CheckBoxOutlined';
import FilterIcon from '@mui/icons-material/FilterAltOutlined';

import { FavouritesPanel } from '../../ClipFavourites/Panel';
import { useClipFavourites } from '../../ClipFavourites/context';
import { useClipFilter } from '../../ClipFilter/context';
import { FilterPanel } from '../../ClipFilter/Panel';
import { useDrawingMarkers } from '../../VideoDrawing/context';
import favouriteStyles from '../../ClipFavourites/Favourites.module.scss';
import {
  COMMENT,
  TOPIC,
  COMMENT_PARENT_TYPE,
  NOTIFICATION,
  VIDEOFEED_TYPE,
  VIDEOFEED,
  COLLABORATIVE_FOLDER_TYPE,
} from '../../../enums';
import { updateSortedSelectedClips } from '../../../store/videos/videoSlice';
import MinimizeIcon from '../../../assets/icons/minimizeIcon.svg';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  deleteComment,
  getComposerDataByVideoFeedIdForVideoComments,
  getFolderById,
  updateComment,
} from '../../../api';
import { ACCESS_TYPE_ENUM } from '../../../enums/ACCESS_ENUM';
import { RolesLowerCase } from '../../../enums/ROLES';
import { findSearchValue, timeLineFormatTime } from '../../../helpers';
import { getActiveReplies, compareCommentsByTime, compareCommentsByTimestamp, isVisibleComment, CommentDrawingFilter, matchesCommentDrawingFilter } from '../../../helpers/comments';
import styles from './VideoComments.module.scss';
import LinearProgress from '../LinearProgress';
import Footer from './CommentFooter';
import Comment from './Comment';
import Modal from '../Modal';
import { ContainedButton, OutlinedButton } from '../Buttons';
import SendClipModal from '../../Timeline/SendClipModal';
import NewComposerModal from '../../Timeline/NewComposerModal';
import { sendCommentToComposer, updateDrawingMarker } from '../../../api/commentsApi';
import { getComposerWebPreferences } from '../../../helpers/localStorage';
import { ClipCommentHeader } from './ClipCommentHeader';
import { CommentTargetCard } from './CommentTargetCard';
import { clipLabel, commentCandidates, CommentClip, containsPlayhead, visibleCommentClips } from './clipComments';

enum COMMENT_LOGIC {
  ALL = 'ALL',
  POPPING = 'POPPING',
}

export const SidebarToggle = ({ expanded, onClick }: { expanded: boolean; onClick: () => void }) => (
  <button type="button" className={styles.sidebarToggle} onClick={onClick} aria-expanded={expanded}
    aria-label={expanded ? 'Minimize sidebar' : 'Expand sidebar'} title={expanded ? 'Minimize sidebar' : 'Expand sidebar'}>
    {expanded && <span>Minimize sidebar</span>}
    <img src={MinimizeIcon} alt="" />
  </button>
);

const VideoComments = ({ switchCommentsView }: { switchCommentsView: () => void }) => {
  const { markers: drawingMarkers, showMarker, showVideoComment, refresh: refreshDrawingMarkers, discussionRequest, dismissDiscussion, seekSequence } = useDrawingMarkers();
  const isTimeline = useMemo(() => window.location.href.includes('/Timeline'), [window.location.href]);
  const { sidebarView, setSidebarView, refreshComments } = useClipFilter();
  const { setPlaybackList, setWorkspace, editor, actions, playerSeek, clipEntry } = useClipFavourites();
  useEffect(() => {
    setPlaybackList(sidebarView === 'selected' || sidebarView === 'favourites' ? sidebarView : null);
    return () => setPlaybackList(null);
  }, [sidebarView, setPlaybackList]);
  const [commentList, setCommentList] = useState<COMMENT[] | null>(null);
  const [drawingFilter, setDrawingFilter] = useState<CommentDrawingFilter>('all');
  const videoDrawingMarkers = useMemo(() => drawingMarkers.filter((comment) => comment.clip_id == null && comment.parent_type !== COMMENT_PARENT_TYPE.CLIP), [drawingMarkers]);
  const [comments, setComments] = useState<COMMENT[]>([]);
  const [allTopics, setAllTopics] = useState<TOPIC[]>([]);
  const [second, setSecond] = useState<string>('0');
  const [feedType, setFeedType] = useState<VIDEOFEED_TYPE>();
  const [disableComment, setDisableComment] = useState(false);
  const [loading, setLoading] = useState(false);
  const { videoPlayedSeconds, sortedSelectedClips, videoFeed, refreshTrigger, markerPosition, videoLength, videoControls } = useAppSelector(
    (state) => state.videoStore,
  );
  const {
    currentSelectedComposerClips,
    composerPlayControl,
    customTime,
    composers,
    currentComposerData,
    refreshTrigger: composerRefreshTrigger,
    commentFetchTrigger,
  } = useAppSelector((state) => state.composersStore);
  const videoCommentList = useMemo(() => [
    ...(commentList ?? (!isTimeline ? currentComposerData?.comments : undefined) ?? [])
      .filter((comment) => !comment.drawing && isVisibleComment(comment)),
    ...videoDrawingMarkers,
  ], [isTimeline, commentList, currentComposerData?.comments, videoDrawingMarkers]);
  const feedId = findSearchValue('feedId');
  const discussionClipId = discussionRequest?.comment?.clip_id ?? discussionRequest?.clipId ??
    (discussionRequest?.comment?.parent_type === COMMENT_PARENT_TYPE.CLIP ? discussionRequest.comment.parent_id : undefined);
  const { currentUser } = useAppSelector((state) => state.user);
  const [section, setSection] = useState<COMMENT_PARENT_TYPE>(COMMENT_PARENT_TYPE.CLIP);
  const notification = useSnackbar();
  const dispatch = useAppDispatch();
  const [videoFeedData, setVideoFeedData] = useState<VIDEOFEED>();
  const [commentToSend, setCommentToSend] = useState<COMMENT | null>(null);
  const [showCommentClipRange, setShowCommentClipRange] = useState(false);
  const [commentClipRange, setCommentClipRange] = useState({ inSeconds: '10', outSeconds: '10' });
  const [showCommentComposerPicker, setShowCommentComposerPicker] = useState(false);
  const [showNewCommentComposer, setShowNewCommentComposer] = useState(false);
  const commentSendOperation = useRef<{ key: string; id: string } | null>(null);
  const { currentVideoThatInFrame } = useAppSelector((state) => state.sharedStore);
  const isContentAdminOA = findSearchValue('allContent') === 'true' && currentUser?.role?.title === RolesLowerCase.ORGANIZATION_ADMIN;
  const commentSource = (!isTimeline ? currentComposerData : null) || videoFeedData || videoFeed.find((feed) => String(feed.id) === String(feedId));
  const canCreateCommentClip = !!currentUser && (
    currentUser.role?.title === RolesLowerCase.ORGANIZATION_ADMIN ||
    String(commentSource?.created_by?.id) === String(currentUser.id) ||
    [ACCESS_TYPE_ENUM.COMMENT_AND_CREATE, ACCESS_TYPE_ENUM.COMMENT_CREATE_AND_RESHARE, ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE]
      .includes(commentSource?.highest_user_access?.access?.title as ACCESS_TYPE_ENUM)
  );
  const openCommentClipComposer = (comment: COMMENT) => {
    commentSendOperation.current = null;
    setCommentToSend(comment);
    setCommentClipRange({ inSeconds: '10', outSeconds: '10' });
    setShowCommentClipRange(true);
  };
  const clipInSeconds = Number(commentClipRange.inSeconds);
  const clipOutSeconds = Number(commentClipRange.outSeconds);
  const commentClipRangeValid = !!commentClipRange.inSeconds.trim() && !!commentClipRange.outSeconds.trim() &&
    Number.isFinite(clipInSeconds) && Number.isFinite(clipOutSeconds) &&
    clipInSeconds >= 0 && clipOutSeconds >= 0 && clipInSeconds + clipOutSeconds > 0;
  const sendCommentClip = (destination: { destinationId?: string; title?: string }) => {
    if (!commentToSend) return Promise.reject(new Error('Select a video comment first'));
    const preferences = getComposerWebPreferences();
    const data = {
      ...destination,
      ...(destination.destinationId !== undefined && { destinationId: String(destination.destinationId) }),
      ...(destination.title !== undefined && { title: destination.title.trim() }),
      videoId: commentToSend.drawing?.videoId || (currentVideoThatInFrame?.id ? String(currentVideoThatInFrame.id) : undefined),
      inSeconds: clipInSeconds,
      outSeconds: clipOutSeconds,
      addOnBottom: preferences.sendClipsAddOnBottom,
    };
    const key = JSON.stringify([commentToSend.id, data]);
    if (commentSendOperation.current?.key !== key) {
      commentSendOperation.current = { key, id: crypto.randomUUID() };
    }
    return sendCommentToComposer(String(commentToSend.id), { ...data, clientOperationId: commentSendOperation.current.id });
  };

  const noClips = useMemo(() => (videoFeedData?.topics ?? []).every((t) => (t?.clips?.length ?? 0) === 0), [videoFeedData]);

  const timesValid = useMemo(() => {
    const len = Number(videoLength);
    const inRange = (n: any) => Number.isFinite((n = Number(n))) && n >= 0 && n <= len;
    if (!Number.isFinite(len) || len <= 0) return false;

    return (videoFeedData?.topics ?? []).every((t) => (t?.clips ?? []).every((c) => inRange(c.time_start) || inRange(c.time_end)));
  }, [videoFeedData, videoLength]);
  const disableClipCommentSection = useMemo(() => noClips || !timesValid, [noClips, timesValid]);

  useEffect(() => {
    if (isTimeline && disableClipCommentSection) {
      setSection(COMMENT_PARENT_TYPE.VIDEOFEED);
    }
  }, [disableClipCommentSection, isTimeline]);

  const autoPickSection = useRef(true);
  useEffect(() => {
    autoPickSection.current = true;
  }, [feedId]);

  useEffect(() => {
    if (!isTimeline || !autoPickSection.current) return;
    setSection(disableClipCommentSection ? COMMENT_PARENT_TYPE.VIDEOFEED : COMMENT_PARENT_TYPE.CLIP);
  }, [disableClipCommentSection, isTimeline]);

  const [parentFolder, setParentFolder] = useState<COLLABORATIVE_FOLDER_TYPE | null>();
  const [rootFolder, setRootFolder] = useState<COLLABORATIVE_FOLDER_TYPE | null>();

  const folderId = findSearchValue('folderId');
  const folderTrail = findSearchValue('folderTrail');
  const rootFolderId = folderTrail ? decodeURIComponent(folderTrail).split(',').filter(Boolean)[0] : null;
  const isCollaboratorInFolder = (folder?: COLLABORATIVE_FOLDER_TYPE | null) =>
    !!currentUser &&
    !!folder &&
    ((folder.collaborators || []).some((u) => u.email === currentUser.email) ||
      !!(folder.group_collaborators || []).some((g) =>
        [...(g.analysts || []), ...(g.viewers || [])].some((u) => u.email === currentUser.email),
      ));

  const fetchFolderAccessContext = async () => {
    const [parentRes, rootRes] = await Promise.all([
      getFolderById(folderId!),
      rootFolderId && rootFolderId !== folderId ? getFolderById(rootFolderId) : Promise.resolve(null),
    ]);

    if (parentRes?.data) {
      setParentFolder(parentRes.data);
    }

    if (rootRes?.data) {
      setRootFolder(rootRes.data);
    } else {
      setRootFolder(null);
    }
  };

  useEffect(() => {
    if (folderId) {
      fetchFolderAccessContext();
    }
  }, [folderId, rootFolderId]);

  const commentsRequest = useRef(0);
  const fetchVideofeed = async (id: string) => {
    const requestId = ++commentsRequest.current;
    try {
      setLoading(true);

      const { data } = await getComposerDataByVideoFeedIdForVideoComments(id);
      if (requestId !== commentsRequest.current) return;
      setVideoFeedData(data);

      setAllTopics(data.topics);
      setCommentList([...data.comments]);
      updateList(data.comments);
    } catch (error) {
      console.error(error);
    } finally {
      if (requestId === commentsRequest.current) setLoading(false);
    }
  };

  const fetchDataHelper = (id: string) => {
    fetchVideofeed(id);
  };

  const onCommentMutation = (id: string) => {
    refreshComments();
    refreshDrawingMarkers();
    fetchDataHelper(id);
  };

  const updateList = (comment: COMMENT[]) => {
    if (comment) {
      const commentsByTimestamp = comment?.filter((x) => +x.timestamp?.toFixed() <= +second);
      setComments(commentsByTimestamp);
    }
  };

  useEffect(() => {
    setCommentList(null);
    if (feedId) {
      fetchDataHelper(feedId);
    }
  }, [feedId]);

  useEffect(() => {
    const searchfeedTypeValue = findSearchValue('pageType');
    if (searchfeedTypeValue?.includes(VIDEOFEED_TYPE.TIMELINE)) {
      setFeedType(VIDEOFEED_TYPE.TIMELINE);
    } else if (searchfeedTypeValue?.includes(VIDEOFEED_TYPE.MY_COMPOSER)) {
      setFeedType(VIDEOFEED_TYPE.MY_COMPOSER);
    } else if (searchfeedTypeValue?.includes(VIDEOFEED_TYPE.COMPOSER)) {
      setFeedType(VIDEOFEED_TYPE.COMPOSER);
    }
  }, [feedId, refreshTrigger, composerRefreshTrigger]);

  useEffect(() => {
    if (commentFetchTrigger && feedId) {
      fetchDataHelper(feedId);
    }
  }, [commentFetchTrigger]);

  useEffect(() => {
    if (isTimeline) {
      setSecond(videoPlayedSeconds?.toFixed());
    } else {
      setSecond(composerPlayControl.composerControls.playedSeconds?.toFixed());
    }
  }, [videoPlayedSeconds, composerPlayControl.composerControls.playedSeconds, sortedSelectedClips[0]?.id]);

  const [commentLogic, setCommentLogic] = useState(COMMENT_LOGIC.ALL);
  const selection = isTimeline ? sortedSelectedClips : currentSelectedComposerClips;
  const selectedIds = new Set(selection.map((clip) => String(clip.id)));
  const playheadSeconds = isTimeline ? (videoControls.playing ? videoControls.playedSeconds : videoPlayedSeconds)
    : composerPlayControl.composerControls.playedSeconds;
  const playing = isTimeline ? videoControls.playing : composerPlayControl.composerControls.playing;
  const commentClips = useMemo<CommentClip[]>(() => {
    const loaded = new Map(allTopics.flatMap((topic) => topic.clips).map((clip) => [String(clip.id), clip.comments]));
    return (editor.rows.length ? editor.rows : allTopics).flatMap((row, rowIndex) =>
      row.clips.map((clip, clipIndex) => ({ ...clip, rowIndex, clipIndex, topicId: row.id, topicName: row.name,
        comments: loaded.get(String(clip.id)) || clip.comments || [],
      })));
  }, [editor.rows, allTopics]);
  const clipsAtPlayhead = commentClips.filter((clip) => containsPlayhead(clip, playheadSeconds));
  const draggingPlayhead = useAppSelector((state) => state.sharedStore.isDraggingCursor);
  const [expiredEntry, setExpiredEntry] = useState<typeof clipEntry>(null);
  const manualSeek = useRef(seekSequence);
  const reachedEntry = useRef<typeof clipEntry>(null);
  // A navigation entry can arrive before the player has committed the seek.
  const enteredClip = clipEntry && clipsAtPlayhead.find((clip) => String(clip.id) === clipEntry.id);
  const manuallySeeking = draggingPlayhead || (isTimeline && videoControls.seeking) || manualSeek.current !== seekSequence;
  useEffect(() => {
    manualSeek.current = seekSequence;
    if (clipEntry && (manuallySeeking || (!enteredClip && reachedEntry.current === clipEntry))) {
      setExpiredEntry(clipEntry);
    }
    if (enteredClip) reachedEntry.current = clipEntry;
  }, [clipEntry, enteredClip, manuallySeeking, seekSequence]);
  const explicitClip = !manuallySeeking && clipEntry !== expiredEntry ? enteredClip || undefined : undefined;
  const scopedCandidates = commentCandidates(commentClips, selectedIds, playheadSeconds);
  const candidates = explicitClip && !scopedCandidates.includes(explicitClip)
    ? [...scopedCandidates, explicitClip] : scopedCandidates;
  const candidateKey = JSON.stringify([candidates.map((clip) => clip.id), clipEntry?.revision, seekSequence]);
  const [pickedClip, setPickedClip] = useState<{ key: string; id: string } | null>(null);
  useEffect(() => { setPickedClip(null); }, [candidateKey, feedId, section, manuallySeeking]);
  useEffect(() => { if (!selection.length) setCommentLogic(COMMENT_LOGIC.ALL); }, [selection.length]);
  const targetClip = candidates.length === 1 ? candidates[0]
    : (!manuallySeeking && pickedClip?.key === candidateKey ? candidates.find((clip) => String(clip.id) === pickedClip.id) : undefined) || explicitClip;
  const playingClip = clipsAtPlayhead.find((clip) => isTimeline
    ? clip.rowIndex === markerPosition.rowIndex && clip.clipIndex === markerPosition.clipIndex
    : clip.rowIndex === composerPlayControl.currentRowIndex && clip.clipIndex === composerPlayControl.currentClipIndex)
    || candidates[0] || clipsAtPlayhead[0];
  const visibleClips = visibleCommentClips(commentClips, selectedIds, commentLogic === COMMENT_LOGIC.POPPING,
    drawingFilter, playingClip && String(playingClip.id), discussionClipId != null ? String(discussionClipId)
      : explicitClip?.comments.some(isVisibleComment) ? String(explicitClip.id) : undefined);
  const commentedCount = commentClips.filter((clip) => clip.comments.some(isVisibleComment)).length;
  const shownCommentedCount = visibleClips.filter((clip) => clip.comments.some((comment) => matchesCommentDrawingFilter(comment, drawingFilter))).length;
  const filtered = selectedIds.size > 0 || drawingFilter !== 'all';
  const [postedCommentId, setPostedCommentId] = useState<string | null>(null);
  const commentListRef = useRef<HTMLDivElement>(null);
  const activeSelectedId = playingClip && selectedIds.has(String(playingClip.id)) &&
    visibleClips.includes(playingClip) ? String(playingClip.id) : undefined;
  useEffect(() => {
    if (sidebarView !== 'comments' || section !== COMMENT_PARENT_TYPE.CLIP || !activeSelectedId) return;
    const group = Array.from(commentListRef.current?.querySelectorAll<HTMLElement>('[data-comment-clip-id]') || [])
      .find((node) => node.dataset.commentClipId === activeSelectedId);
    group?.firstElementChild?.scrollIntoView?.({ block: 'nearest' });
  }, [activeSelectedId, section, sidebarView]);
  useEffect(() => {
    if (!postedCommentId) return;
    const element = Array.from(commentListRef.current?.querySelectorAll<HTMLElement>('[data-comment-id]') || [])
      .find((node) => node.dataset.commentId === postedCommentId);
    if (element) { element.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); setPostedCommentId(null); }
  }, [postedCommentId, allTopics, videoCommentList]);

  useEffect(() => {
    if (section === COMMENT_PARENT_TYPE.VIDEOFEED) setComments(videoCommentList.filter((comment) =>
      matchesCommentDrawingFilter(comment, drawingFilter) && (commentLogic === COMMENT_LOGIC.ALL || comment.timestamp <= +second)));
  }, [second, videoCommentList, section, commentLogic, drawingFilter]);

  const urlParams = document.location.search;
  const commentIdFromQuery = findSearchValue('commentId');
  const replyIdFromQuery = findSearchValue('replyId');
  const commentTabFromQuery = findSearchValue('commentTab');
  const [autoExpandCommentId, setAutoExpandCommentId] = useState<string | null>(null);
  const [autoExpandReplyId, setAutoExpandReplyId] = useState<string | null>(null);
  useEffect(() => {
    if (feedId) {
      const feedToCheck = videoFeed.find((f) => f.id === feedId);
      const composerCheck = composers.find((c) => c.id === feedId);
      const currentComposer = !isTimeline && currentComposerData?.id === feedId ? currentComposerData : null;
      const currentFeedData = videoFeedData?.id === feedId ? videoFeedData : null;
      const activeContent = currentComposer || currentFeedData || feedToCheck || composerCheck;
      const effectiveAccessTitle = currentComposer
        ? currentComposer.highest_user_access?.access?.title
        : currentFeedData?.highest_user_access?.access?.title ||
        feedToCheck?.highest_user_access?.access?.title ||
        composerCheck?.highest_user_access?.access?.title;

      if (
        effectiveAccessTitle === ACCESS_TYPE_ENUM.VIEW_AND_COMMENT ||
        effectiveAccessTitle === ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE ||
        effectiveAccessTitle === ACCESS_TYPE_ENUM.COMMENT_AND_CREATE ||
        effectiveAccessTitle === ACCESS_TYPE_ENUM.COMMENT_CREATE_AND_RESHARE
      ) {
        setDisableComment(false);
      } else if (
        effectiveAccessTitle === ACCESS_TYPE_ENUM.VIEW ||
        effectiveAccessTitle === ACCESS_TYPE_ENUM.NO_ACCESS
      ) {
        setDisableComment(true);
      } else if (activeContent && activeContent?.created_by?.email != currentUser?.email) {
        setDisableComment(true);
      } else {
        setDisableComment(false);
      }

      if (isCollaboratorInFolder(parentFolder) || isCollaboratorInFolder(rootFolder)) {
        setDisableComment(false);
      }

      if (isContentAdminOA) {
        setDisableComment(false);
      }


    }
  }, [
    urlParams,
    feedId,
    videoFeed,
    composers,
    currentComposerData,
    currentUser,
    parentFolder,
    rootFolder,
    isTimeline,
    section,
    videoFeedData,
    isContentAdminOA,
  ]);

  useEffect(() => {
    if (isTimeline) {
    } else {
      dispatch(updateSortedSelectedClips([]));
    }
  }, [feedType]);

  const handleDelete = async (comment: COMMENT) => {
    if (!feedId) return;
    if (getActiveReplies(comment).length && !window.confirm('Delete this comment? Replies will remain under “Comment deleted”.')) return;
    try {
      setLoading(true);
      await deleteComment(comment.id, feedId);
      refreshComments();
      refreshDrawingMarkers();
      return await fetchVideofeed(feedId as string);
    } catch (error) {
      notification.showMessage(NOTIFICATION.NOT_DELETED);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (comment: COMMENT, newMessage: string) => {
    if (!feedId) return;
    try {
      setLoading(true);
      if (comment.drawing) await updateDrawingMarker(comment.id, { message: newMessage });
      else await updateComment(comment.id, { newMessage });
      refreshComments();
      refreshDrawingMarkers();
      notification.showMessage(NOTIFICATION.SUCCESSFUL_MESSAGE_CHANGE);
      return await fetchVideofeed(feedId as string);
    } catch (error) {
      notification.showMessage(NOTIFICATION.NOT_EDITED);
    } finally {
      setLoading(false);
    }
  };

  const setCommentSection = (commentSection: COMMENT_PARENT_TYPE) => {
    autoPickSection.current = false;
    setSection(commentSection);
  };

  useEffect(() => {
    if (!discussionRequest) return;
    const comment = discussionRequest.comment;
    setCommentSection(discussionRequest.clipId != null || comment?.clip_id != null || comment?.parent_type === COMMENT_PARENT_TYPE.CLIP
      ? COMMENT_PARENT_TYPE.CLIP : COMMENT_PARENT_TYPE.VIDEOFEED);
    setCommentLogic(COMMENT_LOGIC.ALL); setDrawingFilter('all');
    setAutoExpandCommentId(comment ? String(comment.id) : null);
  }, [discussionRequest]);

  useEffect(() => {
    if (!commentIdFromQuery) {
      setAutoExpandCommentId(null);
      setAutoExpandReplyId(null);
      return;
    }

    setAutoExpandCommentId(commentIdFromQuery);
    setAutoExpandReplyId(replyIdFromQuery || null);

    if (isTimeline) {
      const commentInFeed = videoCommentList.find((comment) => String(comment.id) === String(commentIdFromQuery));
      let commentInClip = false;
      if (!commentInFeed && allTopics?.length) {
        commentInClip = allTopics.some((topic) =>
          topic?.clips?.some((clip) => clip?.comments?.some((comment) => String(comment.id) === String(commentIdFromQuery))),
        );
      }
      setCommentLogic(COMMENT_LOGIC.ALL);
      if (commentTabFromQuery === 'video' || commentInFeed) {
        setCommentSection(COMMENT_PARENT_TYPE.VIDEOFEED);
      } else if (commentInClip) {
        setCommentSection(COMMENT_PARENT_TYPE.CLIP);
      }
    } else {
      setCommentLogic(COMMENT_LOGIC.ALL);
    }
  }, [commentIdFromQuery, replyIdFromQuery, commentTabFromQuery, videoCommentList, allTopics, isTimeline]);

  const jumpToClip = (clip: CommentClip) => {
    dismissDiscussion(); setAutoExpandCommentId(null);
    actions.current?.seek(clip, playing, clip.time_start, true);
    playerSeek.current?.(clip.time_start);
  };
  const toggleClip = (clip: CommentClip) => {
    if (selectedIds.has(String(clip.id))) actions.current?.deselect(clip);
    else actions.current?.select(clip, true);
    jumpToClip(clip);
    setCommentLogic(COMMENT_LOGIC.ALL);
  };
  const showAll = () => {
    dismissDiscussion();
    setExpiredEntry(clipEntry);
    actions.current?.deselect(selection);
    setCommentLogic(COMMENT_LOGIC.ALL); setDrawingFilter('all');
  };
  const onPosted = (comment: COMMENT) => {
    if (!matchesCommentDrawingFilter(comment, drawingFilter)) setDrawingFilter('all');
    setCommentLogic(COMMENT_LOGIC.ALL);
    dismissDiscussion();
    setPostedCommentId(String(comment.id));
  };

  return (
    <div className={cx(styles.comments, sidebarView === 'comments' && styles.commentsMode)} onPointerDownCapture={() => setWorkspace('sidebar')}>
      <div className={styles.commentsHeader}>
        <div className={styles.heading}>
          <div className={cx(favouriteStyles.tabs, favouriteStyles.compactTabs)} role="group" aria-label="Sidebar controls">
            <div className={favouriteStyles.tabList} role="tablist" aria-label="Sidebar view">
              <Tooltip title="Comments"><button className={favouriteStyles.tab} type="button" role="tab" aria-label="Comments" aria-selected={sidebarView === 'comments'} onClick={() => setSidebarView('comments')}>
                <CommentOutlinedIcon /> <span className={favouriteStyles.tabLabel}>Comments</span>
              </button></Tooltip>
              <Tooltip title="Selected clips"><button className={favouriteStyles.tab} type="button" role="tab" aria-label="Selected clips" aria-selected={sidebarView === 'selected'} onClick={() => setSidebarView('selected')}>
                <SelectedIcon /> <span className={favouriteStyles.tabLabel}>Selected</span>
              </button></Tooltip>
              <Tooltip title="Favourites"><button className={favouriteStyles.tab} type="button" role="tab" aria-label="Favourites" aria-selected={sidebarView === 'favourites'} onClick={() => setSidebarView('favourites')}>
                <StarOutlinedIcon /> <span className={favouriteStyles.tabLabel}>Favourites</span>
              </button></Tooltip>
              <Tooltip title="Filter clips"><button className={favouriteStyles.tab} type="button" role="tab" aria-label="Filter" aria-selected={sidebarView === 'filter'} onClick={() => setSidebarView('filter')}>
                <FilterIcon /> <span className={favouriteStyles.tabLabel}>Filter</span>
              </button></Tooltip>
            </div>
          </div>
        </div>
      </div>
      {sidebarView === 'filter' ? <FilterPanel /> : sidebarView !== 'comments' ? (
        <FavouritesPanel
          key={sidebarView}
          disableComments={disableComment}
          onCommentsChange={() => onCommentMutation(feedId!)}
          selectedClips={sidebarView === 'selected' ? (isTimeline ? sortedSelectedClips : currentSelectedComposerClips) : undefined}
          playhead={isTimeline ? {
            ...markerPosition,
            seconds: videoControls.playing ? videoControls.playedSeconds : videoPlayedSeconds,
            playing: videoControls.playing,
          } : {
            rowIndex: composerPlayControl.currentRowIndex,
            clipIndex: composerPlayControl.currentClipIndex,
            seconds: composerPlayControl.composerControls.playedSeconds > 0
              ? composerPlayControl.composerControls.playedSeconds : customTime,
            playing: composerPlayControl.composerControls.playing,
          }}
        />
      ) : <>
        <div className={styles.commentsToggle} role="group" aria-label="Comment type" onKeyDown={(event) => event.stopPropagation()}>
          <button type="button" className={cx(styles.commentButton, section === COMMENT_PARENT_TYPE.VIDEOFEED && styles.activeCommentButton)}
            aria-pressed={section === COMMENT_PARENT_TYPE.VIDEOFEED} onClick={() => setCommentSection(COMMENT_PARENT_TYPE.VIDEOFEED)}>Video comments</button>
          <button type="button" className={cx(styles.commentButton, section === COMMENT_PARENT_TYPE.CLIP && styles.activeCommentButton)}
            disabled={isTimeline && disableClipCommentSection} aria-pressed={section === COMMENT_PARENT_TYPE.CLIP}
            onClick={() => setCommentSection(COMMENT_PARENT_TYPE.CLIP)}>Clip comments</button>
        </div>

      {section === COMMENT_PARENT_TYPE.VIDEOFEED && (
        <div className={styles.commentLogic} onKeyDown={(event) => event.stopPropagation()}>
          <button type="button" aria-pressed={commentLogic === COMMENT_LOGIC.ALL}
            onClick={() => setCommentLogic(COMMENT_LOGIC.ALL)}
            className={cx(styles.logicBtn, commentLogic === COMMENT_LOGIC.ALL ? styles.logicBtnActive : '')}
          >
            All Comments
          </button>
          <button type="button" aria-pressed={commentLogic === COMMENT_LOGIC.POPPING}
            onClick={() => setCommentLogic(COMMENT_LOGIC.POPPING)}
            className={cx(styles.logicBtn, commentLogic === COMMENT_LOGIC.POPPING ? styles.logicBtnActive : '')}
          >
            Time-synced Comments
          </button>
        </div>
      )}

      <div className={styles.compactFilters} onKeyDown={(event) => event.stopPropagation()}>
        {section === COMMENT_PARENT_TYPE.CLIP && <label>Show
          <select aria-label="Show clip comments" disabled={!selection.length} value={selection.length ? commentLogic : COMMENT_LOGIC.ALL}
            onChange={(event) => setCommentLogic(event.target.value as COMMENT_LOGIC)}>
            <option value={COMMENT_LOGIC.ALL}>{selection.length ? `Selected (${selection.length})` : 'All clips'}</option>
            {selection.length > 0 && <option value={COMMENT_LOGIC.POPPING}>Now playing</option>}
          </select>
        </label>}
        <label>Drawings
          <select aria-label="Filter comments by drawings" value={drawingFilter} onChange={(event) => setDrawingFilter(event.target.value as CommentDrawingFilter)}>
            <option value="all">All</option><option value="with">With</option><option value="without">Without</option>
          </select>
        </label>
      </div>
      {section === COMMENT_PARENT_TYPE.CLIP && <div className={cx(styles.filterBanner, filtered && styles.filteredBanner)} role="status">
        <span>{filtered ? `Showing ${shownCommentedCount} of ${commentedCount} commented clips` : `Showing all ${commentedCount} commented clips`}</span>
        {filtered && <button type="button" onClick={showAll}>Show all</button>}
      </div>}

      <div ref={commentListRef} className={styles.commentsWrapper}>
        {section === COMMENT_PARENT_TYPE.CLIP ? <div className={styles.commentsList}>
          {visibleClips.map((clip) => <section key={clip.id} data-comment-clip-id={clip.id} aria-label={clipLabel(clip)}>
            <ClipCommentHeader clip={clip} selected={selectedIds.has(String(clip.id))} atPlayhead={containsPlayhead(clip, playheadSeconds)}
              playing={playing} onJump={() => jumpToClip(clip)} onToggle={() => toggleClip(clip)} />
            {clip.comments.some((comment) => matchesCommentDrawingFilter(comment, drawingFilter)) ? clip.comments
              .filter((comment) => matchesCommentDrawingFilter(comment, drawingFilter)).slice().sort(compareCommentsByTime).map((comment) =>
                <div key={comment.id} data-comment-id={comment.id}>
                  <Comment onCreateClip={canCreateCommentClip ? openCommentClipComposer : undefined} videofeedId={feedId!}
                    fetch={onCommentMutation} comment={comment} onEditSubmit={handleEditSubmit} onDelete={handleDelete} disable={disableComment}
                    autoEdit={String(comment.id) === String(discussionRequest?.comment?.id) && discussionRequest?.edit}
                    revealSequence={String(comment.id) === String(discussionRequest?.comment?.id) ? discussionRequest?.sequence : undefined}
                    autoExpand={(!!autoExpandReplyId && String(comment.id) === String(autoExpandCommentId)) || String(comment.id) === String(discussionRequest?.comment?.id)} />
                </div>) : <p className={styles.emptyComments}>{clip.comments.some(isVisibleComment) ? 'No comments match this filter.' : 'No comments on this clip yet.'}</p>}
          </section>)}
          {!visibleClips.length && <p className={styles.emptyComments}>{loading ? 'Loading comments…' : 'No clips match this view.'}</p>}
        </div> : comments.slice().sort(compareCommentsByTimestamp).map((comment) => <div key={comment.id} data-comment-id={comment.id}>
          {isFinite(comment.timestamp) && <button type="button" className={cx(styles.clipHeader, styles.videoCommentTime)}
            onClick={() => comment.drawing ? showMarker(comment) : showVideoComment(comment)}>{timeLineFormatTime(comment.timestamp)}</button>}
          <Comment onCreateClip={canCreateCommentClip ? openCommentClipComposer : undefined} videofeedId={feedId!} fetch={onCommentMutation}
            comment={comment} onEditSubmit={handleEditSubmit} onDelete={handleDelete} disable={disableComment}
            autoEdit={String(comment.id) === String(discussionRequest?.comment?.id) && discussionRequest?.edit}
            revealSequence={String(comment.id) === String(discussionRequest?.comment?.id) ? discussionRequest?.sequence : undefined}
            autoExpand={(!!autoExpandReplyId && String(comment.id) === String(autoExpandCommentId)) || String(comment.id) === String(discussionRequest?.comment?.id)} />
        </div>)}
      </div>

      <div className={styles.footerWrapper} onKeyDown={(event) => event.stopPropagation()}>
        <CommentTargetCard video={section === COMMENT_PARENT_TYPE.VIDEOFEED} candidates={candidates} clip={targetClip}
          selected={!!targetClip && selectedIds.has(String(targetClip.id))}
          onPick={(clip) => setPickedClip({ key: candidateKey, id: String(clip.id) })} />
        {loading && <Box sx={{ width: '100%' }}><LinearProgress /></Box>}
        <Footer videofeedId={feedId!} videoPlayedSeconds={playheadSeconds} fetch={onCommentMutation} disable={disableComment}
          target={section === COMMENT_PARENT_TYPE.VIDEOFEED ? { parentType: COMMENT_PARENT_TYPE.VIDEOFEED, parentId: feedId! }
            : targetClip ? { parentType: COMMENT_PARENT_TYPE.CLIP, parentId: String(targetClip.id) } : null}
          placeholder={section === COMMENT_PARENT_TYPE.CLIP && !targetClip ? candidates.length > 1 ? 'Choose which clip to comment on'
            : selection.length ? 'Move playhead over a selected clip' : 'Move playhead over a clip to comment' : undefined}
          onPosted={onPosted} />
      </div>
      </>}
      <SidebarToggle expanded onClick={switchCommentsView} />
      {showCommentClipRange && <Modal open onClose={() => setShowCommentClipRange(false)}>
        <form className={styles.commentClipRange} onSubmit={(event) => {
          event.preventDefault();
          if (!commentClipRangeValid) return;
          setShowCommentClipRange(false);
          setShowCommentComposerPicker(true);
        }}>
          <h3>Create clip from comment</h3>
          <p>Comment at {timeLineFormatTime(commentToSend!.timestamp)}</p>
          <div className={styles.commentClipRangeFields}>
            <label>Seconds before comment
              <input type="number" min="0" step="any" required autoFocus value={commentClipRange.inSeconds}
                onChange={(event) => setCommentClipRange({ ...commentClipRange, inSeconds: event.target.value })} />
            </label>
            <label>Seconds after comment
              <input type="number" min="0" step="any" required value={commentClipRange.outSeconds}
                onChange={(event) => setCommentClipRange({ ...commentClipRange, outSeconds: event.target.value })} />
            </label>
          </div>
          <div className={styles.commentClipRangeActions}>
            <OutlinedButton onClick={() => setShowCommentClipRange(false)}>Cancel</OutlinedButton>
            <ContainedButton type="submit" disabled={!commentClipRangeValid}>Continue</ContainedButton>
          </div>
        </form>
      </Modal>}
      {showCommentComposerPicker && <SendClipModal open skipConfirmation
        switchModal={(open) => setShowCommentComposerPicker(!!open)}
        sendToComposer={(destinationId) => sendCommentClip({ destinationId })}
        onCreateComposer={() => { setShowCommentComposerPicker(false); setShowNewCommentComposer(true); }} />}
      {showNewCommentComposer && <NewComposerModal open skipConfirmation
        switchModal={() => setShowNewCommentComposer(false)}
        createComposer={(title) => sendCommentClip({ title })} />}
    </div>
  );
};

export default VideoComments;
